import { useMemo } from 'react';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { gql, useQuery } from '@zenbusiness/application-commons-graphql';

import { useLogError } from '#client/hooks/useLogError';
import { BANKING_TRANSITION_SEGMENT } from '#client/pages/BankingTransition/constants';

import { DASHBOARD_PATH } from './constants';

const BANKING_ENTITLEMENT = 'banking';

// Accounts holding an open banking account have 18 business entities at most (Snowflake, 2026-09-21).
// The entitlements lookup below aliases one field per entity, so this must stay at or below
// MAX_ALIASES in server/api/validation/aliasLimit.js or the whole query is rejected.
const ENTITY_PAGE_SIZE = 25;

/**
 * Builds the path to the banking transition flow for a given business entity.
 * @param {string} businessEntityUuid The UUID of the business entity
 * @returns {string} The path to the banking transition flow
 */
export function buildTransitionFlowPath(businessEntityUuid) {
  return `${DASHBOARD_PATH}/${businessEntityUuid}/banking/${BANKING_TRANSITION_SEGMENT}`;
}

export const ACCOUNT_BUSINESS_ENTITIES = gql`
  query BankingTransitionAccountEntities($accountUuid: UUID!, $pageSize: Int!) {
    businessEntities(
      filter: { accountUuid: $accountUuid }
      page: { pageSize: $pageSize }
      sort: { field: name, direction: asc }
    ) {
      edges {
        node {
          uuid
        }
      }
    }
  }
`;

// useQuery needs a valid document even when the lookup is skipped.
const NO_ENTITLEMENTS_QUERY = gql`
  query BankingTransitionEntityEntitlementsEmpty {
    __typename
  }
`;

const aliasFor = (index) => `entity${index}`;
const variableFor = (index) => `uuid${index}`;

/**
 * Aliases one `entitlements(businessEntityUuid:)` field per entity, since the schema cannot read
 * entitlements for several entities at once. Uuids are variables rather than inlined so the query text
 * depends only on the count -- `gql` reports a ZBError when one operation name maps to differing text.
 * @param {number} entityCount How many entities to look up
 * @returns {string} The aliased query, or a placeholder when there is nothing to look up
 */
export const buildEntitlementsQuery = (entityCount) => {
  if (!entityCount) {
    return NO_ENTITLEMENTS_QUERY;
  }

  const indexes = Array.from({ length: entityCount }, (_, index) => index);
  const variables = indexes.map((index) => `$${variableFor(index)}: UUID!`).join(', ');
  const fields = indexes
    .map(
      (index) =>
        `${aliasFor(index)}: entitlements(businessEntityUuid: $${variableFor(index)}) { name }`
    )
    .join('\n    ');

  return gql`
  query BankingTransitionEntityEntitlements${entityCount}(${variables}) {
    ${fields}
  }
`;
};

/**
 * Maps entity uuids onto the variable names `buildEntitlementsQuery` declares.
 * @param {Array<string>} businessEntityUuids Entities to look up, in order
 * @returns {object} Variables keyed `uuid0`, `uuid1`, ...
 */
export const buildEntitlementsVariables = (businessEntityUuids) =>
  Object.fromEntries(businessEntityUuids.map((uuid, index) => [variableFor(index), uuid]));

/**
 * Resolves which entity's transition flow this entity-less deep link should open.
 *
 * `accountUuid` comes from the access token rather than `useIdentity()`, whose `identified` flag waits
 * on a query that swallows its errors -- depending on it would strand this page on a permanent spinner.
 * @returns {{ target: string | null, loading: boolean }} Destination path, or null while resolving
 */
export default function useBankingTransitionTarget() {
  const { accountUuid } = useAuthentication();
  const {
    businessEntityUuid: defaultBusinessEntityUuid,
    entitlements,
    loading: loadingDefaultEntity
  } = useBusinessEntity();

  const defaultEntityHasBanking = Boolean(entitlements?.has(BANKING_ENTITLEMENT));
  const skipLookup = loadingDefaultEntity || defaultEntityHasBanking || !accountUuid;

  const {
    data: entitiesData,
    loading: loadingEntities,
    error: entitiesError,
    errors: entitiesErrors
  } = useQuery(ACCOUNT_BUSINESS_ENTITIES, {
    variables: { accountUuid, pageSize: ENTITY_PAGE_SIZE },
    skip: skipLookup,
    noThrow: true
  });

  const businessEntityUuids = useMemo(
    () => entitiesData?.businessEntities?.edges?.map(({ node }) => node.uuid) ?? [],
    [entitiesData]
  );

  const entitlementsQuery = useMemo(
    () => buildEntitlementsQuery(businessEntityUuids.length),
    [businessEntityUuids.length]
  );

  const entitlementsVariables = useMemo(
    () => buildEntitlementsVariables(businessEntityUuids),
    [businessEntityUuids]
  );

  const {
    data: entitlementsData,
    loading: loadingEntitlements,
    error: entitlementsError,
    errors: entitlementsErrors
  } = useQuery(entitlementsQuery, {
    variables: entitlementsVariables,
    skip: skipLookup || !businessEntityUuids.length,
    noThrow: true
  });

  // `entitlements` is a non-null list, so one failing alias nullifies the whole payload and the
  // customer silently falls through to the dashboard.
  useLogError(entitiesErrors, entitiesError);
  useLogError(entitlementsErrors, entitlementsError);

  if (loadingDefaultEntity) {
    return { target: null, loading: true };
  }

  if (defaultEntityHasBanking) {
    return { target: buildTransitionFlowPath(defaultBusinessEntityUuid), loading: false };
  }

  if (loadingEntities || loadingEntitlements) {
    return { target: null, loading: true };
  }

  const bankingEntityUuid = businessEntityUuids.find((uuid, index) =>
    entitlementsData?.[aliasFor(index)]?.some(({ name }) => name === BANKING_ENTITLEMENT)
  );

  return {
    target: bankingEntityUuid ? buildTransitionFlowPath(bankingEntityUuid) : DASHBOARD_PATH,
    loading: false
  };
}
